<template>
  <div class="currency-widget" @click="openDialog">
    <div v-if="allRates === null" @click="openDialog">
      <v-layout row>
        <v-flex xs12 class="text-xs-center" align-self-center>
          <p>{{ $t('widgets.currency.loading') }}</p>
        </v-flex>
      </v-layout>
    </div>
    <div v-else id="currency-table" @click="openDialog">
      <v-row no-gutters class="currency-row">
        <v-col v-if="showFlags === 1" cols="2">
          <img :src="`./img/flags/${base}.png`" width="38" class="flag" />
        </v-col>
        <v-col :cols="showFlags === 1 ? 6 : 8">
          <span class="currency-iso">{{ base }}</span>
          <span class="currency-name">{{ getCurrencyName(base, 'name') }}</span>
        </v-col>
        <v-col cols="4" class="currency-price base-currency">
          <p v-if="getCurrencyName(base, 'pos') == 1" class="currency-value">
            {{ getCurrencyName(base, 'sym') }}1.00
          </p>
          <p v-else class="currency-value">
            1.00{{ getCurrencyName(base, 'sym') }}
          </p>
        </v-col>
      </v-row>

      <v-row
        v-for="(value, key, index) in allRates"
        :key="index"
        no-gutters
        class="currency-row"
      >
        <v-col v-if="showFlags === 1" cols="2">
          <img :src="`./img/flags/${key}.png`" width="38" class="flag" />
        </v-col>
        <v-col :cols="showFlags === 1 ? 6 : 8">
          <span class="currency-iso">{{ key }}</span>
          <span class="currency-name">{{ getCurrencyName(key, 'name') }}</span>
        </v-col>
        <v-col cols="4" class="currency-price target-currency">
          <p v-if="getCurrencyName(key, 'pos') === 1" class="currency-value">
            {{ getCurrencyName(key, 'sym') }}{{ value.toFixed(2) }}
          </p>
          <p v-else class="currency-value">
            {{ value.toFixed(2) }}{{ getCurrencyName(key, 'sym') }}
          </p>
        </v-col>
      </v-row>
    </div>
    <div class="remove-btn currency">
      <remove-button :widget-position="currentWidget" :widget-name="widgetName" />
    </div>
    <v-dialog v-model="dialog" scrollable max-width="500" dark>
      <widgetForm
        :show-form="showForm"
        :widget-schema="formSchema"
        :widget-values="formValues"
        :widget-name="widgetName"
        :widget-position="currentWidget"
        @closeDialog="closeDialog"
      />
    </v-dialog>
  </div>
</template>

<script>
import axios from 'axios'
import { mapState } from 'vuex'
import WidgetForm from '../../components/Forms/widgetForm'
import RemoveButton from '../../components/Buttons/RemoveButton'
// eslint-disable-next-line

export default {
  name: 'Currency',
  components: {
    WidgetForm,
    RemoveButton
  },
  props: {
    widgetData: {
      type: Object,
      required: false
    },
    widgetName: {
      type: String,
      required: true
    },
    paidPlan: {
      type: Boolean,
      required: false
    }
  },
  data() {
    return {
      loading: true,
      dialog: false,
      showForm: false,
      currentWidget: this.$attrs.position,
      formValues: {},
      formSchema: {},
      base: '',
      currencies: [],
      showFlags: 1,
      allRates: null
    }
  },
  computed: {
    ...mapState('settings', ['settings']),
    useData() {
      const globalCurrency = this.settings.currency
      const selectedBase = this.widgetData.base
      const selectedTo = this.widgetData.currencies
      const willShowFlags = this.widgetData.showFlags
      /* Create defaults for this widget */
      return (
        this.widgetData || {
          updateEvery: 30000,
          base: selectedBase || globalCurrency,
          currency: selectedTo || ['EUR,GBP'],
          showFlags: willShowFlags || false
        }
      )
    },
    getCurrentLang() {
      return this.$i18n.locale
    }
  },
  watch: {
    widgetData: {
      handler(newVal, oldVal) {
        console.log('NEW VAL: ', newVal)
        if (
          (newVal && newVal.base !== oldVal.base) ||
          (newVal && newVal.currencies !== oldVal.currencies) ||
          (newVal && newVal.showFlags !== oldVal.showFlags) ||
          (newVal && newVal.updateEvery !== oldVal.updateEvery)
        ) {
          this.base = newVal.base
          this.currencies = newVal.currencies
          this.showFlags = newVal.showFlags
          this.updateEvery = newVal.updateEvery
          this.getExchange()
        }
      },
      deep: true
    }
  },
  mounted() {
    this.base = this.widgetData.base || ''
    this.currencies = this.widgetData.currencies || ['EUR,GBP']
    this.showFlags = this.widgetData.showFlags || true
    this.getExchange()
  },
  beforeDestroy() {
    clearInterval(this.$options.exchange_interval)
  },
  methods: {
    openDialog() {
      this.dialog = true
      this.setFormSchema()
      this.setFormValues()
      this.showForm = true
    },
    closeDialog() {
      this.dialog = false
      this.showForm = false
      this.setFormValues()
      this.formSchema = {}
    },
    getCurrencyName(key, ask) {
      const currNames = {
        USD: [this.$i18n.t('common.currency.name.USD'), '$', 1],
        EUR: [this.$i18n.t('common.currency.name.EUR'), '€', 1],
        GBP: [this.$i18n.t('common.currency.name.GBP'), '£', 1],
        AUD: [this.$i18n.t('common.currency.name.AUD'), 'AU$', 1],
        BRL: [this.$i18n.t('common.currency.name.BRL'), 'R$', 1],
        BGN: [this.$i18n.t('common.currency.name.BGN'), 'Лв.', 2],
        CNY: [this.$i18n.t('common.currency.name.CNY'), '¥', 1],
        HRK: [this.$i18n.t('common.currency.name.HRK'), 'kn', 2],
        CZK: [this.$i18n.t('common.currency.name.CZK'), 'Kč', 2],
        DKK: [this.$i18n.t('common.currency.name.DKK'), 'Kr.', 1],
        HKD: [this.$i18n.t('common.currency.name.HKD'), 'HK$', 1],
        HUF: [this.$i18n.t('common.currency.name.HUF'), 'Ft.', 2],
        ISK: [this.$i18n.t('common.currency.name.ISK'), 'Íkr', 2],
        INR: [this.$i18n.t('common.currency.name.INR'), '₹', 1],
        IDR: [this.$i18n.t('common.currency.name.IDR'), 'Rp', 1],
        ILS: [this.$i18n.t('common.currency.name.ILS'), '₪', 1],
        JPY: [this.$i18n.t('common.currency.name.JPY'), '¥', 1],
        MYR: [this.$i18n.t('common.currency.name.MYR'), 'RM', 1],
        MXN: [this.$i18n.t('common.currency.name.MXN'), '$', 1],
        NZD: [this.$i18n.t('common.currency.name.NZD'), '$', 1],
        NOK: [this.$i18n.t('common.currency.name.NOK'), 'kr', 1],
        PHP: [this.$i18n.t('common.currency.name.PHP'), '₱', 1],
        PLN: [this.$i18n.t('common.currency.name.PLN'), 'zł', 2],
        RON: [this.$i18n.t('common.currency.name.RON'), 'L', 2],
        RUB: [this.$i18n.t('common.currency.name.RUB'), '₽', 2],
        SGD: [this.$i18n.t('common.currency.name.SGD'), 'S$', 1],
        ZAR: [this.$i18n.t('common.currency.name.ZAR'), 'R', 1],
        KRW: [this.$i18n.t('common.currency.name.KRW'), '₩', 1],
        SEK: [this.$i18n.t('common.currency.name.SEK'), 'kr', 2],
        CHF: [this.$i18n.t('common.currency.name.CHF'), 'fr.', 1],
        THB: [this.$i18n.t('common.currency.name.THB'), '฿', 2],
        TRY: [this.$i18n.t('common.currency.name.TRY'), '₺', 2]
      }
      if (ask === 'pos') {
        return currNames[key][2]
      }
      if (ask === 'sym') {
        return currNames[key][1]
      }
      return currNames[key][0]
    },
    setFormSchema() {
      const newCurr = [
        { text: this.$i18n.t('common.currency.nameSymbol.USD'), value: 'USD' },
        { text: this.$i18n.t('common.currency.nameSymbol.EUR'), value: 'EUR' },
        { text: this.$i18n.t('common.currency.nameSymbol.GBP'), value: 'GBP' },
        { text: this.$i18n.t('common.currency.nameSymbol.AUD'), value: 'AUD' },
        { text: this.$i18n.t('common.currency.nameSymbol.BRL'), value: 'BRL' },
        { text: this.$i18n.t('common.currency.nameSymbol.BGN'), value: 'BGN' },
        { text: this.$i18n.t('common.currency.nameSymbol.CNY'), value: 'CNY' },
        { text: this.$i18n.t('common.currency.nameSymbol.HRK'), value: 'HRK' },
        { text: this.$i18n.t('common.currency.nameSymbol.CZK'), value: 'CZK' },
        { text: this.$i18n.t('common.currency.nameSymbol.DKK'), value: 'DKK' },
        { text: this.$i18n.t('common.currency.nameSymbol.HKD'), value: 'HKD' },
        { text: this.$i18n.t('common.currency.nameSymbol.HUF'), value: 'HUF' },
        { text: this.$i18n.t('common.currency.nameSymbol.ISK'), value: 'ISK' },
        { text: this.$i18n.t('common.currency.nameSymbol.INR'), value: 'INR' },
        { text: this.$i18n.t('common.currency.nameSymbol.IDR'), value: 'IDR' },
        { text: this.$i18n.t('common.currency.nameSymbol.ILS'), value: 'ILS' },
        { text: this.$i18n.t('common.currency.nameSymbol.JPY'), value: 'JPY' },
        { text: this.$i18n.t('common.currency.nameSymbol.MYR'), value: 'MYR' },
        { text: this.$i18n.t('common.currency.nameSymbol.MXN'), value: 'MXN' },
        { text: this.$i18n.t('common.currency.nameSymbol.NZD'), value: 'NZD' },
        { text: this.$i18n.t('common.currency.nameSymbol.NOK'), value: 'NOK' },
        { text: this.$i18n.t('common.currency.nameSymbol.PHP'), value: 'PHP' },
        { text: this.$i18n.t('common.currency.nameSymbol.PLN'), value: 'PLN' },
        { text: this.$i18n.t('common.currency.nameSymbol.RON'), value: 'RON' },
        { text: this.$i18n.t('common.currency.nameSymbol.RUB'), value: 'RUB' },
        { text: this.$i18n.t('common.currency.nameSymbol.SGD'), value: 'SGD' },
        { text: this.$i18n.t('common.currency.nameSymbol.ZAR'), value: 'ZAR' },
        { text: this.$i18n.t('common.currency.nameSymbol.KRW'), value: 'KRW' },
        { text: this.$i18n.t('common.currency.nameSymbol.SEK'), value: 'SEK' },
        { text: this.$i18n.t('common.currency.nameSymbol.CHF'), value: 'CHF' },
        { text: this.$i18n.t('common.currency.nameSymbol.THB'), value: 'THB' },
        { text: this.$i18n.t('common.currency.nameSymbol.TRY'), value: 'TRY' }
      ]
      const formSchema = {
        base: {
          type: 'select',
          label: this.$i18n.t('widgets.currency.form.base'),
          items: newCurr,
          flex: 12
        },
        currencies: {
          type: 'select',
          multiple: true,
          label: this.$i18n.t('widgets.currency.form.toCurrency'),
          items: newCurr,
          flex: 12
        },
        showFlags: {
          type: 'radio',
          label: this.$i18n.t('widgets.currency.form.showFlags'),
          options: [
            {
              label: this.$i18n.t('common.buttons.yes'),
              value: 1
            },
            {
              label: this.$i18n.t('common.buttons.no'),
              value: 0
            }
          ],
          flex: 6
        },
        updateEvery: {
          type: 'select',
          label: this.$i18n.t('widgets.currency.form.updateEvery'),
          items: [
            { text: this.$i18n.t('time.freq.30sec'), value: 30000 },
            { text: this.$i18n.t('time.freq.1min'), value: 60000 },
            { text: this.$i18n.t('time.freq.5min'), value: 300000 },
            { text: this.$i18n.t('time.freq.10min'), value: 600000 },
            { text: this.$i18n.t('time.freq.30min'), value: 180000 },
            { text: this.$i18n.t('time.freq.1h'), value: 360000 },
            { text: this.$i18n.t('time.freq.3h'), value: 1080000 }
          ],
          flex: 12
        }
      }
      this.formSchema = formSchema
    },
    setFormValues() {
      this.formValues.base = this.base
      this.formValues.currencies = this.currencies
      this.formValues.showFlags = this.showFlags
      this.formValues.updateEvery = this.useData.updateEvery || 60000
    },
    getExchange() {
      clearInterval(this.$options.exchange_interval)
      this.getRates()
      this.$options.exchange_interval = setInterval(this.getRates, 60000)
    },
    getRates() {
      axios
        .get(
          `https://api.exchangeratesapi.io/latest?base=${
            this.base
          }&symbols=${this.currencies.toString()}`,
          {}
        )
        .then(response => {
          console.log(response)
          this.allRates = response.data.rates
        })
      this.loading = false
    },
    removeWidget() {
      this.dialog = false
      this.$store.dispatch('dashboard/updateWidgetType', {
        [this.currentWidget]: 'blank'
      })
    }
  }
}
</script>

<style lang="scss">
.currency-widget {
  .remove-btn {
    text-align: center;
    font-size: 12px;
    transition: 0s;
    opacity: 0;
  }
  &:hover {
    cursor: pointer;
    .remove-btn {
      opacity: 0.8;
      transition-delay: 1.5s;
      text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.4);
    }
  }
  #currency-table {
    width: 100%;
    max-width: 300px;
    background-color: transparent;
    color: white;
    text-align: center;
    .currency-row {
      height: 44px;
      .flag {
        padding-top: 4px;
      }
      span {
        margin: 0px;
        text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.4);
        &.currency-iso {
          height: 24px;
          display: block;
          text-align: left;
          font-size: 22px;
          color: rgb(255, 255, 255);
        }
        &.currency-name {
          display: block;
          text-align: left;
          font-size: 12px;
          color: rgba(255, 255, 255, 0.75);
        }
      }
      .currency-price {
        margin-top: 10px;
        p {
          text-shadow: 1px 1px 1px rgba(0, 0, 0, 0.4);
          &.currency-value {
            text-align: right;
            font-size: 22px;
            color: rgb(255, 255, 255);
          }
        }
      }
    }
  }
}
</style>

<style lang="scss">
.widget-top {
  &.widget-left {
    .currency-widget {
      #currency-table {
        // float: left;
      }
    }
  }
  &.widget-center {
    .currency-widget {
      #currency-table {
        margin: 0 auto;
      }
    }
  }
  &.widget-right {
    .currency-widget {
      #currency-table {
        float: right;
      }
    }
  }
}
.widget-middle {
  &.widget-left {
    .currency-widget {
      #currency-table {
        // float: left;
      }
    }
  }
  &.widget-center {
    .currency-widget {
      #currency-table {
        margin: 0 auto;
      }
    }
  }
  &.widget-right {
    .currency-widget {
      #currency-table {
        float: right;
      }
    }
  }
}
.widget-bottom {
  &.widget-left {
    .currency-widget {
      #currency-table {
        // float: left;
      }
    }
  }
  &.widget-center {
    .currency-widget {
      #currency-table {
        margin: 0 auto;
      }
    }
  }
  &.widget-right {
    .currency-widget {
      #currency-table {
        margin-right: 20px;
        // float: right;
      }
    }
  }
}
</style>
